<template>
  <div class="user-name-display">
    <div class="user-name-display__name-row">
      <RouterLink
        v-if="username"
        :to="`/profile/${username}`"
        class="user-name-display__name user-name-display__name--link"
        @click.stop
      >
        {{ displayName || "Unknown" }}
      </RouterLink>
      <span v-else class="user-name-display__name">{{ displayName || "Unknown" }}</span>
      <v-icon
        v-if="verified"
        icon="mdi-check-decagram"
        size="14"
        class="user-name-display__verified"
      />
      <FeaturedBadge
        v-if="featuredBadge"
        :badge="featuredBadge"
        :size="14"
        class="user-name-display__featured-badge"
      />
    </div>
    <div class="user-name-display__handle-row">
      <RouterLink
        v-if="username"
        :to="`/profile/${username}`"
        class="user-name-display__handle user-name-display__handle--link"
        @click.stop
      >
        @{{ username }}
      </RouterLink>
      <span v-else class="user-name-display__handle">@{{ username || "unknown" }}</span>
      <span class="user-name-display__separator">·</span>
      <time class="user-name-display__time">{{ formattedTime }}</time>
      <template v-if="groupName && groupName.toLowerCase() !== 'default'">
        <span class="user-name-display__separator">·</span>
        <RouterLink
          v-if="groupUlid"
          :to="`/groups/${groupUlid}`"
          class="user-name-display__group user-name-display__group--link"
          @click.stop
        >
          {{ groupName }}
        </RouterLink>
        <span v-else class="user-name-display__group">{{ groupName }}</span>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import FeaturedBadge from '@/components/badges/FeaturedBadge.vue';
import type { Badge } from '@/types';

interface Props {
  displayName?: string | null;
  username?: string | null;
  verified?: boolean;
  featuredBadge?: Badge | null;
  time?: Date | string | null;
  groupName?: string | null;
  groupUlid?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  displayName: null,
  username: null,
  verified: false,
  featuredBadge: null,
  time: null,
  groupName: null,
  groupUlid: null,
});

const formattedTime = computed(() => {
  if (!props.time) return '';

  const date = typeof props.time === 'string' ? new Date(props.time) : props.time;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
});
</script>

<style scoped lang="scss">
.user-name-display {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  position: relative; // For absolute positioning of handle
  flex: 1; // Take available space
  min-width: 0; // Allow shrinking

  // When used in post-card context, keep column layout (name on top, handle below)
  .post-card__user-names & {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    width: 100%;
  }
}

.user-name-display__name-row {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.user-name-display__name {
  color: rgb(var(--v-theme-on-surface));
  font-size: 0.9375rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &--link {
    text-decoration: none;
    transition: color 0.15s ease;

    &:hover {
      color: rgb(var(--v-theme-primary));
    }
  }
}

.user-name-display__verified {
  color: rgb(var(--v-theme-primary));
  flex-shrink: 0;
}

.user-name-display__featured-badge {
  flex-shrink: 0;
}

.user-name-display__handle-row {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
  overflow: hidden;
}

.user-name-display__handle {
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &--link {
    text-decoration: none;
    transition: color 0.15s ease;

    &:hover {
      color: rgb(var(--v-theme-primary));
    }
  }
}

.user-name-display__separator {
  color: rgba(var(--v-theme-on-surface), 0.5);
  font-size: 0.875rem;
  flex-shrink: 0;
}

.user-name-display__time {
  color: rgba(var(--v-theme-on-surface), 0.5);
  font-size: 0.875rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.user-name-display__group {
  color: rgba(var(--v-theme-on-surface), 0.5);
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &--link {
    text-decoration: none;
    transition: color 0.15s ease;

    &:hover {
      color: rgb(var(--v-theme-primary));
      text-decoration: underline;
    }
  }
}
</style>
